import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { loadLowBattReport } from '../../../redux/actions/reportsActions';
import InlineHelpText from '../../shared/InlineHelpText';
import InlineAlertText from '../../shared/InlineAlertText';
import SvgOpenExternal from '../../svg/OpenExternal';
import HelpIcon from '../../shared/HelpIcon';
import Table from '../../shared/table/Table';
import AccessControl from '../../../AccessControl';
import {
  FormattedMessage,
  useIntl,
} from 'react-intl';
import permissions from '../../../permissionsList';
import {
  descriptionText,
  searchText,
  categoryText,
  lastCheckinText,
  battTypeText,
  firstBatteryLowText, 
} from '../../shared/SharedInternationalizations';
import LoadingSpinner from '../../shared/LoadingSpinner';
import Pager from '../../shared/Pager';
import SVGBatteryAlert from '../../svg/BatteryAlert';
import { createCSV} from '../../../utilities/general';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
} from '../../shared/Button';
import { csvDate, DateTime, findGap } from '../../shared/Date';

export function UnconnectedLowBatteryDevices(props) {
  const {
    lowbatteryDevices: lowbatteryDevicesArr,
    alarmDeviceTypes,
    apiSpinner,
  } = props;

  useEffect(() => {
    props.dispatch(loadLowBattReport());
  }, []); // eslint-disable-line

  //Access the Intl API for formatting dates into CSV file
  const intl = useIntl();

  // setting up state and function for the pager
  const [pagerParams, setPagerParams] = useState({
    totalLength: 0,
    startValue: 0,
    endValue: 49,
    incrementValue: 50,
  });
  const [devicesFilter, setDevicesFilter] = useState('');

  function setPager(startValue) {
    const pagerValues = { ...pagerParams };
    let start = startValue;
    if (start <= 0) {
      pagerValues.startValue = 0;
      pagerValues.endValue = pagerParams.incrementValue - 1;
    } else {
      pagerValues.startValue = start;
      pagerValues.endValue = startValue + pagerParams.incrementValue - 1;
    }
    setPagerParams(pagerValues);
  }

  const devicesSearchFilter = (event) => {
    const value = event.target.value;
    const pagerValues = { ...pagerParams };
    pagerValues.startValue = 0;

    setPagerParams(pagerValues);
    setDevicesFilter(value);
  };

  const [
    showLowBatteryDevicesHelpText,
    setShowLowBatteryDevicesHelpText,
  ] = useState(false);
  const lowbatteryDevicesHelpTextRef = useRef();
  function toggleLowBatteryDevicesHelpText(value) {
    setShowLowBatteryDevicesHelpText(value);
  }

  const lowbatteryDevicesHelpText = (
    <FormattedMessage
      id="lowbatteryDevices.lowbatteryDevicesHelpText"
      defaultMessage="These are alarm devices that have a low battery."
      description="Help text explaining who the assigned users are"
    />
  );

  // const [
  //   showCriticalBatteryDevicesHelpText,
  //   setShowCriticalBatteryDevicesHelpText,
  // ] = useState(false);
  const criticalBatteryDevicesHelpTextRef = useRef();
  // function toggleCriticalBatteryDevicesHelpText(value) {
  //   setShowCriticalBatteryDevicesHelpText(value);
  // }

  const criticalBatteryDevicesHelpText = (
    <FormattedMessage
      id="lowbatteryDevices.criticalBatteryDevicesHelpText"
      defaultMessage="These are alarm devices that have a critically low battery. If you wait to replace the battery you risk permanent damage to the device."
      description="Help text explaining who the assigned users are"
    />
  );

  const csvHeaders = [
    'Id',
    'Description',
    'Category',
    'Last Checkin',
    'Critical Battery?',
    'First Low Battery',
    'Battery Type',
    'Serial Id',
  ];

  let csvData = [];

  const alarmDevicesTable = () => {
    const alarmDeviceDetailsList = [];
    lowbatteryDevicesArr.map((device) => {
      const deviceLink = (
        <AccessControl
          data-test="access-control"
          feature={permissions.static.devices.alarm.editAlarmDevice}>
          <FormattedMessage
            id="lowbatteryDevices.viewDeviceText"
            defaultMessage="View Device"
            description="Button to view full unit info">
            {(txt) => (
              <div className="width-100 flex-end height-100 align-center">
                <a
                  data-test="view-alarm-device-link"
                  className="text-small margin-right-10 link-text align-center"
                  href={`/admin/alarm_dev_edit_new.php?edit_row[alarm_device_id]=${device.alarm_device_id}&edit_row[alarm_device_type]=${device.alarm_device_type}&orig_type= 1`}>
                  {txt} <SvgOpenExternal className="margin-left-5" />
                </a>
              </div>
            )}
          </FormattedMessage>
        </AccessControl>
      );

      alarmDeviceDetailsList.push({
        id: device.alarm_device_id,
        description: device.alarm_description,
        type: device.alarm_device_type,
        view: deviceLink,
        lastCheckIn: device.last_check_in,
        critical_alarm: device.critical_alarm,
        additional_info: device.additional_info,
        first_battery_low: device.first_battery_low,
        shutdown_declaration: device.shutdown_declaration,
        serial_id: device.serial_id,
      });

      return '';
    });

    // map over the units and put into the format that is required for the table component
    if (alarmDeviceDetailsList.length > 0) {
      const devicesArray = alarmDeviceDetailsList.map((d) => {
        let deviceType = '';
        if (alarmDeviceTypes[d.type]) {
          deviceType = alarmDeviceTypes[d.type].description;
        }
        const checkinGap = ((new Date() - new Date(d.lastCheckIn)) / 1000) * -1; // converts the difference to a negative number for the relative time calculation
        const criticalAlarm = (
          <div className="text-extra-large align-center vertical-center height-100">
            <SVGBatteryAlert
              onClick={() =>
                criticalBatteryDevicesHelpTextRef.current.toggleHelpText()
              }
              className="text-alert cursor-pointer"
            />
          </div>
        );

        //const firstBatteryLowGap = ((new Date() - new Date(d.first_battery_low)) / 1000) * -1; // converts the difference to a negative number for the relative time calculation
          const firstBatteryLowGap = findGap(d.first_battery_low);
        //Rely on d.shutdown_declaration as defined in the original php code, do not use the shutdown_declared field in low_battery_history
        //let shutdownDateAvailable = d.shutdown_declaration && (!isNaN(new Date(d.shutdown_declaration).getTime())) 

        // converts the difference to a negative number for the relative time calculation
        //const shutdownGap = shutdownDateAvailable ? ((new Date() - new Date(d.shutdown_declaration)) / 1000) * -1 : '';
        //const lastCheckInFormatted = intlDatetimeForCsv(new Date(d.lastCheckIn), intl);
        const lastCheckInFormatted = csvDate(d.lastCheckIn, intl);
        //const firstBatteryLowFormatted = intlDatetimeForCsv(new Date(d.first_battery_low), intl);
        const firstBatteryLowFormatted =  csvDate(d.first_battery_low, intl);

        csvData.push([
          d.id,
          d.description,
          deviceType,
          lastCheckInFormatted,
          d.critical_alarm ? 'Yes' : '',
          firstBatteryLowFormatted,
          d.additional_info ? d.additional_info.batt_type : '',
          d.serial_id,
        ]);

        return {
          critical: {
            sortBy: d.critical_alarm,
            display: d.critical_alarm ? criticalAlarm : '',
            style: d.critical_alarm ? 'alert_background--danger' : '',
          },
          description: {
            sortBy: d.critical_alarm
              ? '00' + d.description.toUpperCase()
              : d.description.toUpperCase(),
            display: d.description,
            style: d.critical_alarm ? 'alert_background--danger' : '',
          },
          type: {
            sortBy: deviceType.toUpperCase(),
            display: deviceType,
            style: d.critical_alarm ? 'alert_background--danger' : '',
          },
          last_checkin: {
            sortBy: new Date(d.lastCheckIn),
            /*display: (
              <React.Fragment>
                <FormattedDate
                  value={new Date(d.lastCheckIn)}
                  year="numeric"
                  month="long"
                  day="2-digit"
                />
                <span className="margin-left-10"></span>
                <FormattedTime value={new Date(d.lastCheckIn)} />
                <br></br>
                <FormattedRelativeTime
                  value={checkinGap}
                  updateIntervalInSeconds={60}
                />
              </React.Fragment>
            ),*/
            display:<DateTime date= {d.lastCheckIn} dateGap = {checkinGap} />,
            style: d.critical_alarm ? 'alert_background--danger' : '',
          },
          first_battery_low: {
            sortBy: new Date(d.first_battery_low),
            /*display:(
              <React.Fragment>
                <FormattedDate
                  value={new Date(d.first_battery_low)}
                  year="numeric"
                  month="long"
                  day="2-digit"
                />
                <span className="margin-left-10"></span>
                <FormattedTime value={new Date(d.first_battery_low)} />
                <br></br>
                <FormattedRelativeTime
                  value={firstBatteryLowGap}
                  updateIntervalInSeconds={60}
                />
              </React.Fragment>
            ),*/
            display:<DateTime date= {d.first_battery_low} dateGap = {firstBatteryLowGap} />,
            style: d.critical_alarm ? 'alert_background--danger' : '',
          },
          /* REMOVE
          //Rely on d.shutdown_declaration as defined in the original php code, do not use the shutdown_declared field in low_battery_history
          shutdownDeclared: {
            sortBy: shutdownDateAvailable 
              ? new Date(d.shutdown_declaration)
              : null,

            display: shutdownDateAvailable  ? (
                <React.Fragment>
                  <FormattedDate
                    value={new Date(d.shutdown_declaration)}
                    year="numeric"
                    month="long"
                    day="2-digit"
                  />
                  <span className="margin-left-10"></span>
                  <FormattedTime value={new Date(d.shutdown_declaration)} />
                  <br></br>
                  <FormattedRelativeTime
                    value={shutdownGap}
                    updateIntervalInSeconds={60}
                  />
                </React.Fragment>
            ) : (
              ''
            ),
            style: d.critical_alarm ? 'alert_background--danger' : '',
          },
          */
          battType: {
            sortBy:
              d.additional_info && d.additional_info.batt_type
                ? d.additional_info.batt_type.toUpperCase()
                : '',
            display:
              d.additional_info && d.additional_info.batt_type
                ? d.additional_info.batt_type
                : '',
            style: d.critical_alarm ? 'alert_background--danger' : '',
          },

          view: {
            sortBy: '',
            display: d.view,
            style: d.critical_alarm ? 'alert_background--danger' : '',
          },
        };
      });

      let searchedDevices = [];
      // Take the array and filter based on the text entered in the search bar.
      if (devicesFilter !== '') {
        searchedDevices = devicesArray.filter((item) => {
          let filterResult = false;
          // Make the device name uppercase for case insensitivity
          const description = item.description.display.toUpperCase();
          const type = item.type.display.toUpperCase();

          // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
          if (
            description.includes(devicesFilter.toUpperCase()) ||
            type.includes(devicesFilter.toUpperCase())
          ) {
            filterResult = true;
          }
          return filterResult;
        });
      } else {
        searchedDevices = devicesArray;
      }

      // Take the array and filter based on the text entered in the search bar.
      if (searchedDevices.length !== pagerParams.totalLength) {
        const pagerValues = { ...pagerParams };
        pagerValues.totalLength = searchedDevices.length;
        setPagerParams(pagerValues);
      }

      let pagedDevices = [];
      searchedDevices.map((d, index) => {
        if (index >= pagerParams.startValue && index <= pagerParams.endValue) {
          pagedDevices.push(d);
        }
        return '';
      });

      const tableHeadings = [
        {
          isSortable: true,
          sortBy: 'critical',
          display: 'Critical',
        },
        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
        {
          isSortable: true,
          sortBy: 'type',
          display: categoryText,
        },
        {
          isSortable: true,
          sortBy: 'last_checkin',
          display: lastCheckinText,
        },
        {
          isSortable: true,
          sortBy: 'first_battery_low',
          display: firstBatteryLowText,
        },
        /*
        {
          isSortable: true,
          sortBy: 'shutdownDeclared',
          display: shutdownDeclarationText,
        },
        */
        {
          isSortable: true,
          sortBy: 'battType',
          display: battTypeText,
        },
        {
          isSortable: false,
          sortBy: 'view',
          display: '',
        },
      ];
      // generates the actual table
      const table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="description"
          tableData={pagedDevices}
          tableStyles="table-layout__7-column text-small"
          data-test="low-battery-devices-table"
        />
      );
      return table;
    } else {
      return (
        <span className="text-dimmed margin-right-10">
          <FormattedMessage
            data-test="no-dormant-alarm-devices"
            id="lowBatteryDevices.noDevices"
            defaultMessage="No Low Battery Devices Found"
            description="The user's contact phone number"
          />
        </span>
      );
    }
  };

  function tableDisplay() {
    if (lowbatteryDevicesArr.length > 0 || apiSpinner.lowBattReportLoaded) {
      return alarmDevicesTable();
    } else {
      return (
        <div className="loading-spinner__user-list-container">
          <LoadingSpinner />
        </div>
      );
    }
  }
  function triggerCSV() {
    return createCSV(csvHeaders, csvData, 'low_battery_devices');
  }

  return (
    <React.Fragment>
      <div className="item-container__main-text text-large text-light flex-spread margin-bottom-10">
        <div className="align-center">
          <FormattedMessage
            id="lowbatteryDevices.lowbatteryDevicesText"
            defaultMessage="Low Battery Devices Report:"
            description="The assigned alert devices for this unit"
          />
        </div>
        <div className="align-center">
          <HelpIcon
            onClick={() =>
              lowbatteryDevicesHelpTextRef.current.toggleHelpText()
            }
            toggled={showLowBatteryDevicesHelpText}
            styles="margin-left-5"
          />
        </div>
      </div>
      <InlineHelpText
        ref={lowbatteryDevicesHelpTextRef}
        toggleFunction={toggleLowBatteryDevicesHelpText}
        message={lowbatteryDevicesHelpText}
      />
      <InlineAlertText
        ref={criticalBatteryDevicesHelpTextRef}
        // toggleFunction={toggleCriticalBatteryDevicesHelpText}
        message={criticalBatteryDevicesHelpText}
      />
      <div className="table__header padding-10 flex-spread">
        <div className="vertical-center">
          <label className="text-medium text-bold" htmlFor="usersFilter">
            {searchText}:
          </label>
          <input
            type="text"
            id="usersFilter"
            name="usersFilter"
            onChange={devicesSearchFilter}
            className="message-table__filter-search-box margin-left-10"
            value={devicesFilter}
          />
        </div>
        <Button
          onClick={triggerCSV}
          text="CSV"
          buttonType={buttonTypes.secondaryReversed}
          buttonSize={buttonSizes.small}
          buttonTextSize={buttonTextSizes.extraSmall}
        />
      </div>
      {tableDisplay()}
      {pagerParams.totalLength > 0 ? (
        <Pager
          totalLength={pagerParams.totalLength}
          startValue={pagerParams.startValue}
          incrementValue={pagerParams.incrementValue}
          onClick={setPager}
        />
      ) : (
        ''
      )}
    </React.Fragment>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    lowbatteryDevices: state.reports.devices.lowBatt,
    alarmDeviceTypes: state.devices.alarmDeviceTypes,
    apiSpinner: state.ui.apiSpinner,
  };
};

export default connect(mapStateToProps)(UnconnectedLowBatteryDevices);
